import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { bibleService } from '../../services/bible';
import { historyService } from '../../services/history';
import HeaderMenu from '../../components/bible/HeaderMenu';
import HistoryModal from '../../components/bible/HistoryModal';
import BookmarkModal from '../../components/bible/BookmarkModal';
import SettingsModal from '../../components/bible/SettingsModal';
import LanguagePromptModal from '../../components/bible/LanguagePromptModal';
import ContextSearchModal from '../../components/bible/ContextSearchModal';
import { t, tCount } from '../../i18n/uiText';
import {
  fetchLanguagesList,
  getLanguageCodeForTranslationId,
  getStoredTranslationOrDefault,
} from '../../utils/bibleLocaleSync';
import { isWebAuthenticated, jsonHeadersWithAuth } from '../../utils/webAuth';
import { AUTH_REQUIRED_EVENT, requestAuthPopup } from '../../utils/authPopup';
import { resolveAiLimitMessage } from '../../utils/aiLimitMessage';
import { useTelegramOverlayBack } from '../../hooks/useTelegramOverlayBack';
import { TG_OVERLAY_Z } from '../../services/telegramBackStack';
import { BfPageLoader, useBfFeatures } from '../../components/bible/bf';

export default function BookView() {
  const { enabled: bfEnabled } = useBfFeatures();
  const { translation: translationParam, bookId } = useParams();
  const translation = translationParam || localStorage.getItem('bibleTranslation') || getStoredTranslationOrDefault();
  const navigate = useNavigate();
  const location = useLocation();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chapters');
  const [interpretation, setInterpretation] = useState(null);
  const [interpretationLoading, setInterpretationLoading] = useState(false);
  const [interpretationError, setInterpretationError] = useState(null);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const headerMenuOpenedAtRef = useRef(0);
  const skipNextBurgerClickRef = useRef(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false);
  const [showContextSearch, setShowContextSearch] = useState(false);

  useTelegramOverlayBack('bookHeaderMenu', showHeaderMenu, () => setShowHeaderMenu(false), TG_OVERLAY_Z.headerMenu);
  useTelegramOverlayBack('bookHistory', showHistory, () => setShowHistory(false), TG_OVERLAY_Z.history);
  useTelegramOverlayBack('bookSettings', showSettingsModal, () => setShowSettingsModal(false), TG_OVERLAY_Z.settingsModal);
  useTelegramOverlayBack('bookLanguage', showLanguagePrompt, () => setShowLanguagePrompt(false), TG_OVERLAY_Z.languagePrompt);
  useTelegramOverlayBack('bookSearch', showContextSearch, () => setShowContextSearch(false), TG_OVERLAY_Z.contextSearch);
  useTelegramOverlayBack('bookBookmarks', showBookmarks, () => setShowBookmarks(false), TG_OVERLAY_Z.bookmarks);

  useEffect(() => {
    const anyModalOpen = showLanguagePrompt || showSettingsModal || showHistory || showBookmarks || showContextSearch;
    if (anyModalOpen) {
      setShowHeaderMenu(false);
      document.body.classList.add('bible-modal-chrome-hidden');
      // Блокируем фон, когда открыт любой popup на экране книги.
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('bible-modal-chrome-hidden');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('bible-modal-chrome-hidden');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showLanguagePrompt, showSettingsModal, showHistory, showBookmarks, showContextSearch]);

  useEffect(() => {
    const onAuthRequired = () => {
      setShowHeaderMenu(false);
      setShowContextSearch(false);
      setShowSettingsModal(false);
      setShowHistory(false);
      setShowBookmarks(false);
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!localStorage.getItem('bibleLanguage')) {
      setShowLanguagePrompt(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function ensurePreferredTranslation() {
      if (typeof window === 'undefined') return;
      const storedLanguage = String(localStorage.getItem('bibleLanguage') || '').toLowerCase();
      if (!storedLanguage || !translation) return;

      const languages = await fetchLanguagesList();
      if (cancelled || !languages?.length) return;

      const translationLanguage = getLanguageCodeForTranslationId(translation, languages);
      if (!translationLanguage || translationLanguage === storedLanguage) return;

      const preferredLanguage = languages.find((lang) => lang.code === storedLanguage);
      if (!preferredLanguage) return;

      const storedTranslation = localStorage.getItem('bibleTranslation');
      const preferredTranslation =
        (storedTranslation &&
          Array.isArray(preferredLanguage.translations) &&
          preferredLanguage.translations.some((item) => item.id === storedTranslation) &&
          storedTranslation) ||
        preferredLanguage.defaultTranslation;

      if (!preferredTranslation || preferredTranslation === translation) return;

      const parts = location.pathname.split('/').filter(Boolean);
      if (parts.length === 0) {
        navigate('/' + preferredTranslation, { replace: true });
        return;
      }

      parts[0] = preferredTranslation;
      navigate('/' + parts.join('/'), { replace: true });
    }

    void ensurePreferredTranslation();
    return () => {
      cancelled = true;
    };
  }, [translation, location.pathname, navigate]);

  const headerMenuNow = useCallback(
    () =>
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now(),
    []
  );

  const HEADER_MENU_BACKDROP_GRACE_MS = 650;

  useLayoutEffect(() => {
    if (showHeaderMenu) {
      headerMenuOpenedAtRef.current = headerMenuNow();
    }
  }, [showHeaderMenu, headerMenuNow]);

  const handleHeaderMenuBackdropTouchStart = useCallback(
    (e) => {
      if (headerMenuNow() - headerMenuOpenedAtRef.current < HEADER_MENU_BACKDROP_GRACE_MS) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [headerMenuNow]
  );

  const handleHeaderMenuBackdropDismiss = useCallback(
    (e) => {
      if (headerMenuNow() - headerMenuOpenedAtRef.current < HEADER_MENU_BACKDROP_GRACE_MS) {
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
        return;
      }
      setShowHeaderMenu(false);
    },
    [headerMenuNow]
  );

  useEffect(() => {
    loadBook();
    setActiveTab('chapters');
    setInterpretation(null);
  }, [bookId, translation]);
  
  useEffect(() => {
    if (book) {
      // Сохраняем в историю (localStorage + бэкенд)
      historyService.saveHistory(
        location.pathname, 
        book.name,
        bookId ? parseInt(bookId) : null,
        null,
        null
      );
    }
  }, [location.pathname, book, bookId]);

  useEffect(() => {
    if (activeTab === 'description' && !interpretation && !interpretationLoading) {
      loadInterpretation();
    }
  }, [activeTab]);

  const loadBook = async () => {
    setLoading(true);
    try {
      const data = await bibleService.getBook(bookId, translation);
      setBook(data);
    } catch (error) {
      console.error('Error loading book:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInterpretation = async () => {
    setInterpretationLoading(true);
    setInterpretationError(null);
    const apiBase = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';
    const language = localStorage.getItem('bibleLanguage') || 'en';
    const returnPath = `${location.pathname}${location.search}`;
    if (!isWebAuthenticated()) {
      setInterpretationLoading(false);
      requestAuthPopup(returnPath, true);
      return;
    }
    try {
      const response = await fetch(`${apiBase}/interpret`, {
        method: 'POST',
        headers: jsonHeadersWithAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ bookId: parseInt(bookId), language }),
      });
      if (response.status === 401) {
        requestAuthPopup(returnPath, true);
        throw new Error(t('signInRequiredForAi'));
      }
      if (response.status === 429) {
        const payload = await response.json().catch(() => null);
        if (payload?.code === 'DAILY_AI_LIMIT_REACHED') {
          throw new Error(resolveAiLimitMessage(payload, t, 'interpretation'));
        }
      }
      if (!response.ok) throw new Error(t('error'));
      const data = await response.json();
      setInterpretation(data);
    } catch (err) {
      setInterpretationError(err.message);
    } finally {
      setInterpretationLoading(false);
    }
  };

  const handleChapterClick = (chapter) => {
    navigate(`/${translation}/${bookId}/${chapter}`);
  };

  if (loading) {
    return bfEnabled ? (
      <BfPageLoader label={t('loading')} />
    ) : (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-semibold">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600 font-semibold">{t('noData')}</p>
      </div>
    );
  }

  const chapters = Array.from({ length: book.chapters }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Фиксированный заголовок в стиле ChapterView: пилл + бейдж + поиск + меню */}
      <div className="chapter-header-fixed chapter-header-ref-style chapter-header-reading">
        <button
          type="button"
          className="chapter-header-pill"
          onClick={() => navigate('/synodal')}
          aria-label={t('bible')}
        >
          <span className="chapter-header-pill-text">
            {book.name} · {tCount('bookChapterCount', book.chapters)}
          </span>
        </button>
        <span className="chapter-header-spacer" aria-hidden="true" />
        <button
          type="button"
          className="chapter-header-icon-btn"
          aria-label={t('search')}
          onClick={() => {
            if (!isWebAuthenticated()) {
              const next = `${location.pathname}${location.search}`;
              requestAuthPopup(next, true);
              return;
            }
            setShowContextSearch(true);
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </button>
        <div className="chapter-header-burger-wrapper">
          <button
            type="button"
            className="chapter-header-burger-button"
            onPointerUp={(e) => {
              if (e.pointerType === 'touch' || e.pointerType === 'pen') {
                e.stopPropagation();
                setShowHeaderMenu((prev) => !prev);
                skipNextBurgerClickRef.current = true;
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (skipNextBurgerClickRef.current) {
                skipNextBurgerClickRef.current = false;
                return;
              }
              setShowHeaderMenu((prev) => !prev);
            }}
            aria-label={t('menu')}
          >
            <svg className="chapter-header-burger-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          {showHeaderMenu && (
            <HeaderMenu
              isOpen={showHeaderMenu}
              onShowHistory={() => { setShowHeaderMenu(false); setShowHistory(true); }}
              onShowBookmarks={() => { setShowHeaderMenu(false); setShowBookmarks(true); }}
              onGoHome={() => { setShowHeaderMenu(false); navigate(`/${translation}`); }}
              onGoSettings={() => { setShowHeaderMenu(false); setShowSettingsModal(true); }}
              onClose={() => setShowHeaderMenu(false)}
            />
          )}
        </div>
      </div>

      <div className="book-view-content" style={{ paddingTop: 'calc(5.5rem + var(--bf-safe-top, 0px))' }}>
        {/* Белая карточка с табами и сеткой глав (как на референсе) */}
        <div className="book-view-card">
          <div className="book-view-tabs">
            <button
              onClick={() => setActiveTab('chapters')}
              className={`book-view-tab ${activeTab === 'chapters' ? 'book-view-tab-active' : ''}`}
            >
              <svg className="book-view-tab-icon book-view-tab-icon-book" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                <path d="M8 7h8"></path>
                <path d="M8 11h8"></path>
              </svg>
              <span>{t('chapters')}</span>
            </button>
            <button
              onClick={() => setActiveTab('description')}
              className={`book-view-tab ${activeTab === 'description' ? 'book-view-tab-active' : ''}`}
            >
              <svg className="book-view-tab-icon book-view-tab-icon-desc" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              <span>{t('description')}</span>
            </button>
          </div>

          <div className="book-view-card-body">
            {activeTab === 'chapters' && (
              <div className="book-view-chapter-grid">
                {chapters.map((chapter) => (
                  <button
                    key={chapter}
                    onClick={() => handleChapterClick(chapter)}
                    className="book-view-chapter-button"
                  >
                    {chapter}
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'description' && (
              <div className="book-interpretation book-view-description">
                {interpretationLoading && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-500 rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-500 font-medium">{t('loadingBookDescription')}</p>
                    <p className="text-slate-400 text-sm mt-1">{t('mayTakeSeconds')}</p>
                  </div>
                )}

                {interpretationError && (
                  <div className="text-center py-12">
                    <p className="text-red-500 font-medium mb-4">{t('error')}: {interpretationError}</p>
                    <button type="button" onClick={loadInterpretation} className="bible-ui-btn bible-ui-btn--primary">
                      {t('retry')}
                    </button>
                  </div>
                )}

                {interpretation && !interpretationLoading && (
                  <div className="space-y-6">
                    {/* Основная информация */}
                    {interpretation.meaning && (
                      <div className="book-section">
                        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                          <span>📜</span> {t('aboutBook')}
                        </h3>
                        <p className="text-slate-700 leading-relaxed mb-4">{interpretation.meaning.summary}</p>
                      </div>
                    )}

                    {/* Контекст */}
                    {interpretation.context && (
                      <div className="book-section">
                        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                          <span>📖</span> {t('bookContext')}
                        </h3>
                        {interpretation.context.bookContext && (
                          <p className="text-slate-700 leading-relaxed mb-3">{interpretation.context.bookContext}</p>
                        )}
                        {interpretation.context.biblicalContext && (
                          <p className="text-slate-600 leading-relaxed">{interpretation.context.biblicalContext}</p>
                        )}
                      </div>
                    )}

                    {/* Богословие */}
                    {interpretation.theological && (
                      <div className="book-section">
                        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                          <span>✝️</span> {t('theology')}
                        </h3>
                        {interpretation.theological.themes?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {interpretation.theological.themes.map((theme, idx) => (
                              <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">
                                {theme}
                              </span>
                            ))}
                          </div>
                        )}
                        {interpretation.theological.christConnection && (
                          <div className="p-4 bg-gradient-to-r from-slate-100 to-white rounded-xl">
                            <p className="text-xs uppercase tracking-wider text-[#B08C3A] font-semibold mb-2">{t('christConnection')}</p>
                            <p className="text-slate-700 leading-relaxed">{interpretation.theological.christConnection}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Культурно-исторический контекст */}
                    {(interpretation.cultural?.hasContent || interpretation.historical?.hasContent) && (
                      <div className="book-section">
                        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                          <span>🏺</span> {t('culture')} / {t('historical')}
                        </h3>
                        {interpretation.cultural?.facts?.map((fact, idx) => (
                          <p key={`c-${idx}`} className="text-slate-600 leading-relaxed mb-2">→ {fact}</p>
                        ))}
                        {interpretation.historical?.facts?.map((fact, idx) => (
                          <p key={`h-${idx}`} className="text-slate-600 leading-relaxed mb-2">→ {fact}</p>
                        ))}
                      </div>
                    )}

                    {/* Применение */}
                    {interpretation.application && (
                      <div className="book-section p-4 bg-gradient-to-r from-green-50 to-white rounded-xl border border-green-100">
                        <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                          <span>💡</span> {t('today')}
                        </h3>
                        <p className="text-slate-700 leading-relaxed">{interpretation.application}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showHeaderMenu && (
        <div
          className="header-menu-backdrop"
          aria-hidden="true"
          onTouchStart={handleHeaderMenuBackdropTouchStart}
          onTouchEnd={handleHeaderMenuBackdropDismiss}
          onClick={handleHeaderMenuBackdropDismiss}
        />
      )}

      {showHistory && (
        <HistoryModal onClose={() => setShowHistory(false)} />
      )}
      {showSettingsModal && (
        <SettingsModal onClose={() => setShowSettingsModal(false)} />
      )}
      {showLanguagePrompt && (
        <LanguagePromptModal onClose={() => setShowLanguagePrompt(false)} />
      )}
      {showContextSearch && (
        <ContextSearchModal translation={translation} onClose={() => setShowContextSearch(false)} />
      )}

      {showBookmarks && (
        <BookmarkModal
          bookId={bookId}
          chapter={null}
          verse={null}
          bookName={book?.name}
          translation={translation}
          allowMove={false}
          onClose={() => setShowBookmarks(false)}
        />
      )}
    </div>
  );
}
